import React, {
  FocusEvent,
  useCallback,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'
import {FieldError} from 'react-hook-form'
import convertUnit from 'lib/unit'
import {
  InputFormValidationKeys,
  InputHandlerParam,
  InputHandlerValue,
} from 'types'

export function useInputHandler<
  TFieldValues extends object = object,
  T = HTMLInputElement | HTMLTextAreaElement
>({
  name,
  form,
  formRules,
  onFocus,
  onBlur,
  onChange,
  allowedCharacters,
  onChangeText,
  onKeyPress,
  refElement,
  multiline,
  autosize,
}: InputHandlerParam<TFieldValues, T>) {
  const [isFocused, setFocused] = useState(false)
  const setLastUpdate = useState<number>()[1]

  const register = useMemo(() => form && form.register(formRules), [
    form,
    formRules,
  ])

  const error = useMemo<FieldError | undefined>(
    // @ts-ignore
    () => (form && name ? form.errors[name] : undefined),
    [form, name],
  )
  const errorType = useMemo(
    () => error?.type as InputFormValidationKeys | undefined,
    [error],
  )
  const showError = useMemo(
    () => errorType && !formRules?.hideErrors?.[errorType],
    [errorType, formRules],
  )

  const handleRef = useCallback(
    (instance: any) => {
      if (instance) {
        register && register(instance)
        refElement.current = instance
      }
    },
    [refElement, register],
  )

  const handleTriggerError = useCallback(() => {
    form && form.trigger(name)
  }, [form, name])

  const handleFocus = useCallback(
    (event: FocusEvent<T>) => {
      onFocus && onFocus(event)
      setFocused(true)
    },
    [onFocus, setFocused],
  )

  const handleAdjustHeight = useCallback(() => {
    if (refElement.current) {
      refElement.current.style.height = '0px'
      refElement.current.style.height = convertUnit(
        refElement.current.scrollHeight,
      )
    }
  }, [refElement])

  const handleBlur = useCallback(
    (event: FocusEvent<T>) => {
      onBlur && onBlur(event)
      handleTriggerError()
      setFocused(false)
    },
    [onBlur, setFocused, handleTriggerError],
  )

  const handleChange = useCallback(
    (event: any) => {
      onChange && onChange(event)
      onChangeText && onChangeText(event.target.value || '')
      setLastUpdate(new Date().getTime())
      error && handleTriggerError()
      autosize && handleAdjustHeight()
    },
    [
      autosize,
      error,
      handleAdjustHeight,
      handleTriggerError,
      onChange,
      onChangeText,
      setLastUpdate,
    ],
  )

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent<T>) => {
      onKeyPress && onKeyPress(event)

      if (
        allowedCharacters &&
        !(typeof allowedCharacters === 'function'
          ? allowedCharacters(event.key)
          : allowedCharacters.test(event.key))
      ) {
        event.preventDefault()
      }

      if (event.key === 'Enter' && !multiline) {
        event.preventDefault()
        refElement.current?.blur()
      }
    },
    [allowedCharacters, multiline, onKeyPress, refElement],
  )

  useLayoutEffect(() => {
    if (autosize) handleAdjustHeight()
  })

  return useMemo<InputHandlerValue<T>>(
    () => ({
      error,
      errorType,
      showError,
      isFocused,
      handleFocus,
      handleBlur,
      handleChange,
      handleKeyPress,
      handleRef,
    }),
    [
      error,
      errorType,
      showError,
      isFocused,
      handleFocus,
      handleBlur,
      handleChange,
      handleKeyPress,
      handleRef,
    ],
  )
}
